import { html, LitElement } from 'lit';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { query, state, property } from 'lit/decorators.js';
import { GzRadioGroupWebComponent } from '../../../../ui/gzRadio';
import { LicenseRenderers } from '../../../../company/common/licenseRenderers/LicenseRenderers';
import { FeatureFlag } from '@nirvana/shared/constants';
import { RadioEvent } from '../../../../ui/gzRadio/types';
import { FontSizes, LineHeights } from '../../../../types/common/types';
import { styles } from '../view/styles';
import { AvailableBundles, BundleItem } from '../../../types/Bundles';

export class LicenseMonthlyOwnBundleFormWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  public get value(): Partial<Record<FeatureFlag, boolean>> {
    if (this.formGroup?.value.ownUseBundle) {
      return {
        [this.formGroup.value.ownUseBundle as string]: true,
      };
    } else {
      return {};
    }
  }

  @query('#own-use-bundles-form-filed')
  ownUseBundle: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('#own-use-bundles-radio-group')
  ownUseBundlesRadioGroup: GzRadioGroupWebComponent;

  @state()
  private formGroup: FormGroup | null;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  public availableBundles: AvailableBundles | undefined;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  public parentCompanyMdrContactInfoRequired: boolean;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  public selectedOption: FeatureFlag | undefined;

  private bundleMap: [FeatureFlag: number] | [] = [];

  private static renderBundlesDetails() {
    const bundlesDescription = LicenseRenderers.getBundlesDescription();
    return html` <gz-text
      fontSize="${FontSizes.HEADING_6}"
      lineHeight="${LineHeights.NORMAL}"
      class="bundles-details-text"
      id="own-use-bundles-details"
    >
      ${bundlesDescription[0]}
      <gz-link type="underline" color="primary" id="own-use-bundles-details-link" href=${bundlesDescription[1]}>
        ${bundlesDescription[2]}
      </gz-link>
    </gz-text>`;
  }

  private static renderParentCompanyMdrContactInfoRequired(parentCompanyMdrContactInfoRequired: boolean, availableBundles: AvailableBundles | undefined) {
    if (!parentCompanyMdrContactInfoRequired) {
      return '';
    }

    if (!availableBundles) {
      return '';
    }

    let hasMdrBundles = false;
    for (const key in availableBundles) {
      if (!availableBundles.hasOwnProperty(key)) {
        continue;
      }
      const bundle = availableBundles[key];
      if (bundle.addons.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE)) {
        hasMdrBundles = true;
      }
    }

    if (!hasMdrBundles) {
      return '';
    }

    const description = LicenseRenderers.getParentCompanyMdrContactInfoRequired();
    return html`<gz-text
      fontSize="${FontSizes.PARAGRAPH_12}"
      lineHeight="${LineHeights.NORMAL}"
      class="bundles-details-text"
      id="own-use-bundles-mdr-restrictions"
    >
      ${description}
    </gz-text>`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(RadioEvent.SELECTION_CHANGED, this.handleChangeEvent.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener(RadioEvent.SELECTION_CHANGED, this.handleChangeEvent);
    super.disconnectedCallback();
  }

  public selectOption(featureFlag: FeatureFlag) {
    this.ownUseBundlesRadioGroup.selectOption(featureFlag);
  }

  public selectFirstOption() {
    if (!this.availableBundles || !this.ownUseBundlesRadioGroup) {
      return;
    }

    const firstOption = Object.values(this.availableBundles)[0] as BundleItem;
    this.selectOption(firstOption.usableFeature);
  }

  public uncheckOptions() {
    if (this.ownUseBundlesRadioGroup) {
      this.ownUseBundlesRadioGroup.setFormValue('');
    }
    this.selectedOption = undefined;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.buildFormGroup();
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      ownUseBundle: this.ownUseBundle,
    });
  }

  shouldUpdate(): boolean {
    return this.availableBundles !== undefined;
  }

  private handleChangeEvent(e) {
    this.selectedOption = e.detail.selectedValue;

    if (!this.bundleMap || !e.detail.selectedValue) {
      return;
    }

    const event = new CustomEvent('radioOwnUseSelectionChanged', {
      detail: {
        selectedValue: e.detail.selectedValue,
        bundleId: this.bundleMap[e.detail.selectedValue],
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  render() {
    return html`
      <div id="wrapper">
        <gz-form-field id="own-use-bundles-form-filed">
          <gz-radio-group
            gz-form-input
            id="own-use-bundles-radio-group"
            groupName="bundle-own-use"
            selectedOption="${this.selectedOption ?? ''}"
          >
            ${this.renderRadioList(this.availableBundles)}
          </gz-radio-group>
          ${LicenseMonthlyOwnBundleFormWebComponent.renderParentCompanyMdrContactInfoRequired(
            this.parentCompanyMdrContactInfoRequired,
            this.availableBundles
          )}
          ${LicenseMonthlyOwnBundleFormWebComponent.renderBundlesDetails()}
        </gz-form-field>
      </div>
    `;
  }

  private renderRadioList(availableBundles) {
    return html`${Object.keys(availableBundles).map(index => {
      const bundle = availableBundles[index];
      this.bundleMap[bundle.usableFeature] = Number(index);

      return html` <gz-radio
          .id="own-use-bundle-${index}"
          name="own-use-bundle"
          .value="${bundle.usableFeature}"
          .label="${LicenseRenderers.getTranslatedBundleName(Number(index))}"
        >
        </gz-radio>
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_12}"
          lineHeight="${LineHeights.NORMAL}"
          class="helper-text mb-1"
          id="description-for-bundle-${index}"
          >${LicenseRenderers.getTranslatedBundleDescription(Number(index))}</gz-text
        >`;
    })}`;
  }
}
